import React from 'react';
import { ArrowRight } from 'lucide-react';
import { HiCheckCircle } from 'react-icons/hi2';
import { Button } from '../../shared';
import { usePaddleCheckout } from '../../../hooks/usePaddleCheckout';
import { themeColorHex } from '../../../constants';
import { cn } from '../../ui/utils';

const PricingBox = ({
  popular,
  name,
  price,
  currency,
  info = '',
  id,
  features = [],
  closePlans,
  interval,
  isCurrent = false
}) => {
  const { openCheckout } = usePaddleCheckout();

  return (
    <div
      className={cn(
        'relative flex h-full flex-col rounded-2xl border bg-white p-6 transition-shadow hover:shadow-md sm:p-7',
        isCurrent && 'border-emerald-400 bg-emerald-50/40 ring-1 ring-emerald-200',
        !isCurrent && popular && 'border-primary shadow-sm ring-1 ring-primary/20',
        !isCurrent && !popular && 'border-border/70'
      )}
    >
      {isCurrent && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-emerald-500 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm">
          Current plan
        </span>
      )}
      {!isCurrent && popular && (
        <span
          className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide text-white shadow-sm"
          style={{ backgroundColor: themeColorHex }}
        >
          Most Popular
        </span>
      )}

      <div className={cn('flex flex-col', (popular || isCurrent) && 'pt-2')}>
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {name}
        </p>

        <div className="mt-3 flex items-end gap-1">
          <p className="text-3xl font-bold text-foreground">
            {price > 0 ? `${currency}${price}` : 'Free'}
          </p>
          {price > 0 && interval && (
            <span className="mb-1.5 text-sm text-muted-foreground">/ {interval}</span>
          )}
        </div>
      </div>

      <div className="my-5 h-px w-full bg-border/70" />

      <ul className="mb-6 flex-1 space-y-3">
        {features.map((feature, index) => (
          <li key={`${id}-feature-${index}`} className="flex items-start gap-2.5 text-sm leading-snug">
            <HiCheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-green-500" aria-hidden />
            <span className="text-foreground/90">{feature}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto">
        {price > 0 ? (
          <Button
            type="button"
            id={`priceBox-${id}`}
            buttonType="primary"
            className="h-11 w-full gap-2"
            onClick={() => {
              openCheckout({ priceId: id });
              closePlans();
            }}
          >
            Buy Now
            <ArrowRight className="h-4 w-4" aria-hidden />
          </Button>
        ) : (
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-800">
            Your current plan
          </div>
        )}
        {info && <p className="mt-3 text-center text-xs leading-relaxed text-muted-foreground">{info}</p>}
      </div>
    </div>
  );
};

export default PricingBox;
